import type { LucideIcon } from 'lucide-react';
import {
  createElement,
  useRef,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';

import { cn } from '@/lib/utils';
import { menuPictogram } from '@/platform/lib/menu-pictograms';

const LONG_PRESS_MS = 500;
const MOVE_CANCEL_PX = 8;

/** Large form of the sidebar pictogram: theme well, one glyph, current page inverted. */
export function NavAppTile({
  label,
  path,
  icon,
  active = false,
  disabled,
  badge,
  onOpen,
  onLongPress,
}: {
  label: string;
  path: string;
  icon?: LucideIcon;
  active?: boolean;
  disabled?: boolean;
  badge?: ReactNode;
  onOpen?: () => void;
  onLongPress?: () => void;
}) {
  const timer = useRef<number | null>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);
  const held = useRef(false);
  const moved = useRef(false);
  const mark = icon ?? menuPictogram(path);

  function clearTimer() {
    if (timer.current != null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }

  function onPointerDown(event: ReactPointerEvent<HTMLButtonElement>) {
    if (disabled || !onLongPress) {
      return;
    }
    held.current = false;
    moved.current = false;
    origin.current = { x: event.clientX, y: event.clientY };
    timer.current = window.setTimeout(() => {
      held.current = true;
      timer.current = null;
      onLongPress();
    }, LONG_PRESS_MS);
  }

  function onPointerMove(event: ReactPointerEvent<HTMLButtonElement>) {
    if (!origin.current) {
      return;
    }
    const dx = event.clientX - origin.current.x;
    const dy = event.clientY - origin.current.y;
    if (dx * dx + dy * dy > MOVE_CANCEL_PX * MOVE_CANCEL_PX) {
      moved.current = true;
      clearTimer();
      origin.current = null;
    }
  }

  function onPointerUp() {
    const wasHeld = held.current;
    const didMove = moved.current;
    clearTimer();
    origin.current = null;
    held.current = false;
    moved.current = false;
    if (!wasHeld && !didMove && !disabled) {
      onOpen?.();
    }
  }

  function onPointerCancel() {
    clearTimer();
    origin.current = null;
    held.current = false;
  }

  return (
    <button
      type="button"
      disabled={disabled}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onClick={onLongPress ? undefined : disabled ? undefined : onOpen}
      className="flex w-full min-w-0 flex-col items-center gap-1.5 bg-transparent p-0 text-center"
    >
      <span
        className={cn(
          'nav-app-squircle relative grid aspect-square w-[88%] place-items-center bg-card text-primary',
          active && 'bg-primary text-primary-foreground',
          disabled && 'bg-muted text-muted-foreground shadow-none',
        )}
        style={{ borderRadius: '22%' }}
      >
        {createElement(mark, { 'aria-hidden': true, className: 'size-7', strokeWidth: 1.75 })}
        {badge}
      </span>
      <span className="line-clamp-2 w-full text-[11px] leading-tight font-semibold text-foreground">
        {label}
      </span>
    </button>
  );
}
